// ===========================================
// Permission slip email — Preview + "Send test to me" (#53)
// ===========================================
// Shows the slip's email exactly as a family gets it: the server renders it with
// the same code as the real send (`renderPermissionSlipEmail`), from the editor's
// UNSAVED title, body and deadline. Two switches pick which email and wording:
//   - Request (first send, Refresh audience) or Reminder (Remind);
//   - a contact with a login, or one without (a family's second contact).
// The link in a preview or test is a sample, never a working one.
//
// "Send test to me" goes to the signed-in admin's own address only; the server
// never reads a recipient from the request.

import { useEffect } from 'react';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RenderedPreviewPanel from '../common/RenderedPreviewPanel';
import { PermissionGate } from '../common/PermissionGate';
import { useSendSlipTestEmail, useSlipEmailPreview } from '../../hooks/usePermissionSlips';
import type { SlipEmailRequest } from '../../api/permissionSlips.api';

export interface SlipEmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  slipId: string;
  /** The editor's current (possibly unsaved) values. */
  draft: { title: string; body: string; deadline: string | null };
}

export default function SlipEmailPreviewDialog({
  open,
  onClose,
  slipId,
  draft,
}: SlipEmailPreviewDialogProps) {
  const [kind, setKind] = useState<SlipEmailRequest['kind']>('REQUEST');
  const [hasLogin, setHasLogin] = useState(true);

  const preview = useSlipEmailPreview(slipId);
  const sendTest = useSendSlipTestEmail(slipId);

  const request: SlipEmailRequest = {
    title: draft.title,
    body: draft.body,
    deadline: draft.deadline,
    kind,
    hasLogin,
  };

  // Re-render whenever the dialog opens or a switch changes. The draft is read
  // as it is at that moment; the dialog is modal, so it cannot change underneath.
  const { mutate } = preview;
  useEffect(() => {
    if (open) mutate(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on open and on the switches only
  }, [open, kind, hasLogin, mutate]);

  // Clear the last render on close, so reopening after an edit never shows the
  // previous email for a frame before the new one loads.
  const close = () => {
    preview.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Preview email</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, v) => v && setKind(v)}
            aria-label="Which email"
          >
            <ToggleButton value="REQUEST">Request</ToggleButton>
            <ToggleButton value="REMINDER">Reminder</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={hasLogin ? 'login' : 'contact'}
            onChange={(_, v) => v && setHasLogin(v === 'login')}
            aria-label="Which wording"
          >
            <ToggleButton value="login">Parent with a login</ToggleButton>
            <ToggleButton value="contact">Contact without a login</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <RenderedPreviewPanel
          title="What families receive"
          subject={preview.data?.subject}
          html={preview.data?.html}
          isLoading={preview.isPending}
          isError={preview.isError}
          maxHeight={560}
        />
      </DialogContent>
      <DialogActions>
        <PermissionGate resource="events" action="write">
          <Button
            startIcon={<SendIcon />}
            disabled={sendTest.isPending}
            onClick={() => sendTest.mutate(request)}
          >
            {sendTest.isPending ? 'Sending…' : 'Send test to me'}
          </Button>
        </PermissionGate>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
